import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  ShieldAlert,
  ArrowRight,
  RotateCcw,
  Wallet,
  Home,
  MessageSquare,
  FileWarning,
  Briefcase,
  GraduationCap,
  CheckCircle2,
  AlertTriangle,
  Lock,
  BarChart3,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getRefusalDecoderAggregates } from "@/lib/refusal-decoder-aggregates.functions";

const FAQS = [
  {
    q: "What is a 214(b) US visa refusal?",
    a: "214(b) is a section of the US Immigration & Nationality Act that lets a consular officer refuse a nonimmigrant visa applicant who hasn't demonstrated strong enough ties to their home country or a clear, credible purpose for their trip. It is the most common F-1 and B1/B2 refusal reason in India.",
  },
  {
    q: "How do I know why I got refused under 214(b)?",
    a: "Officers rarely explain in detail - they hand you a printed 214(b) slip. The decoder above works backwards from your interview experience across six common failure patterns (finances, home ties, communication, DS-160 mismatch, weak study/return intent, and profile red flags) to pinpoint the most likely reason.",
  },
  {
    q: "Can I re-apply immediately after a 214(b) refusal?",
    a: "Technically yes, but practically no. Wait times for refusal visa slots in India are currently about a year out. For instance, a student refused on May 18, 2026, will typically see refusal slots open around late March 2027, with the earliest OFC appointment dates starting after their 1-year mark (e.g., May 19, 2027 onwards). Use that waiting window to diagnose your 214(b) root cause, fix your DS-160 narrative, and practice your delivery before re-booking.",
  },
  {
    q: "Does an AI mock interview tool help with 214(b) recovery?",
    a: "AI can help you practice question phrasing, but it can't catch the hesitation, contradictions, or DS-160 mismatches that actually trigger 214(b). Our Specialists have sat across 1,500+ live prep sessions and know the patterns AI misses.",
  },
  {
    q: "Is my data on this page stored anywhere?",
    a: "For signed-in users, your 12 answers plus your consulate are saved to your private account so we can show aggregate consulate-level insights to future students. No names, contact details, or identifying information are ever displayed publicly - only anonymized percentages.",
  },
  {
    q: "How much does WeLivdIt's Refusal Support plan cost?",
    a: "Refusal Support is ₹7,000 and includes root-cause analysis, 5 escalating mock interviews, DS-160 line-by-line review, and a re-attempt strategy - priced in INR for Indian students, not in US dollars.",
  },
];

export const Route = createFileRoute("/refusal-decoder")({
  head: () => ({
    meta: [
      { title: "214(b) Visa Refusal Decoder - Diagnose Why You Got Refused | WeLivdIt" },
      {
        name: "description",
        content:
          "Free interactive checklist that pinpoints the real reason behind your US visa 214(b) refusal - weak finances, missing home ties, communication, or DS-160 red flags. Get a personalized re-attempt playbook.",
      },
      { property: "og:title", content: "214(b) Visa Refusal Decoder - WeLivdIt" },
      {
        property: "og:description",
        content:
          "Diagnose why you were refused before your next interview. Free interactive checklist from Specialists who've helped 1,500+ students get approved since 2021.",
      },
      { property: "og:url", content: "https://welivdit.com/refusal-decoder" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/refusal-decoder" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "214(b) Visa Refusal Decoder",
              item: "https://welivdit.com/refusal-decoder",
            },
          ],
        }),
      },
    ],
  }),
  component: RefusalDecoderPage,
});

type Category = "finances" | "ties" | "communication" | "ds160" | "intent" | "profile";

type Question = {
  id: string;
  category: Category;
  q: string;
  weight?: number;
};

const QUESTIONS: Question[] = [
  {
    id: "q1",
    category: "finances",
    q: "Were you unable to clearly explain who is funding your education/trip and show 2–3 years of bank statements?",
    weight: 2,
  },
  {
    id: "q2",
    category: "finances",
    q: "Did your sponsor's income look inconsistent with the tuition + living cost (e.g. ITR / paystubs didn't match)?",
  },
  {
    id: "q3",
    category: "ties",
    q: "Did you struggle to explain concrete reasons you'll return home (family, property, job offer, business)?",
    weight: 2,
  },
  {
    id: "q4",
    category: "ties",
    q: "Do you have siblings or immediate family already in the US that the officer may have flagged?",
  },
  {
    id: "q5",
    category: "communication",
    q: "Were your answers long, hesitant, or did you contradict yourself under follow-up?",
    weight: 2,
  },
  {
    id: "q6",
    category: "communication",
    q: "Did the officer stop asking questions after 30–60 seconds and hand you the blue slip?",
  },
  {
    id: "q7",
    category: "ds160",
    q: "Were there mismatches between your DS-160 (universities applied, past visas, work history) and what you said?",
    weight: 2,
  },
  {
    id: "q8",
    category: "ds160",
    q: "Did you list a distant/unrelated field of study compared to your bachelor's - without a clear career story?",
  },
  {
    id: "q9",
    category: "intent",
    q: "Did the university choice feel weak (low rank, no scholarship, no specific reason you picked it)?",
  },
  {
    id: "q10",
    category: "intent",
    q: "Was your career plan post-graduation vague or focused on staying in the US?",
    weight: 2,
  },
  { id: "q11", category: "profile", q: "Do you have gaps in academics/work that you couldn't confidently explain?" },
  {
    id: "q12",
    category: "profile",
    q: "Have you been refused before, and did you re-apply with essentially the same DS-160 and story?",
    weight: 2,
  },
];

const CATEGORY_META: Record<Category, { label: string; icon: typeof Wallet; color: string; fixes: string[] }> = {
  finances: {
    label: "Weak Finances",
    icon: Wallet,
    color: "text-emerald-600 bg-emerald-50 border-emerald-200",
    fixes: [
      "Rebuild the funding narrative - one sponsor, one crystal-clear source, backed by ITR + bank statements.",
      "Have a one-line answer for tuition, living, and total 2-year cost - memorized and delivered in 5 seconds.",
      "If loan-funded, know the loan sanction letter details cold (bank name, amount, collateral).",
    ],
  },
  ties: {
    label: "Weak Home Ties",
    icon: Home,
    color: "text-blue-600 bg-blue-50 border-blue-200",
    fixes: [
      "Build a concrete return-home story - family business, property, job offer, or specific plans in India.",
      "Address US family (siblings/spouse) head-on before the officer brings it up.",
      "Show economic and emotional roots - not just 'my parents are here'.",
    ],
  },
  communication: {
    label: "Communication-Confidence",
    icon: MessageSquare,
    color: "text-amber-600 bg-amber-50 border-amber-200",
    fixes: [
      "Cut every answer to 15–25 seconds. Officers decide in the first 60 seconds.",
      "Stop rehearsed scripts - they sound rehearsed. Learn the structure, not the words.",
      "Do 4–5 mocks with a real Specialist who interrupts and cross-examines like an officer.",
    ],
  },
  ds160: {
    label: "DS-160 Mismatch",
    icon: FileWarning,
    color: "text-red-600 bg-red-50 border-red-200",
    fixes: [
      "Line-by-line DS-160 review - every past visa, address, and travel entry must match your story.",
      "Reconcile any inconsistency between DS-160 and your verbal answers before you enter the consulate.",
      "If your field of study feels 'jumpy', build the bridge on paper (career shift narrative).",
    ],
  },
  intent: {
    label: "Academic-Career Story",
    icon: GraduationCap,
    color: "text-purple-600 bg-purple-50 border-purple-200",
    fixes: [
      "Rebuild university choice - specific professor, program, or research angle. Not just 'ranking'.",
      "Have a post-graduation plan that lives in your home country - sector, company type, salary range.",
      "Kill any language that hints at long-term US stay (H-1B, green card, 'settle').",
    ],
  },
  profile: {
    label: "Profile Red Flags",
    icon: Briefcase,
    color: "text-slate-700 bg-slate-100 border-slate-200",
    fixes: [
      "Address the previous refusal directly - what changed since the last attempt?",
      "Fill gaps: a course, a job, a certification - anything that shows movement.",
      "Do a full refusal analysis with a Specialist before booking the next slot.",
    ],
  },
};

type AuthState = "loading" | "signed-out" | "signed-in";

function RefusalDecoderPage() {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>("loading");
  const [userId, setUserId] = useState<string | null>(null);

  const [answers, setAnswers] = useState<Record<string, "yes" | "no" | "unsure" | undefined>>({});
  const [showResults, setShowResults] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [insightsRefreshKey, setInsightsRefreshKey] = useState(0);

  // Auth check
  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!mounted) return;
      if (!data.user) {
        setAuthState("signed-out");
        return;
      }
      setUserId(data.user.id);
      setAuthState("signed-in");
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (!session) setAuthState("signed-out");
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const totalAnswered = Object.values(answers).filter(Boolean).length;
  const progress = Math.round((totalAnswered / QUESTIONS.length) * 100);

  const scores = useMemo(() => {
    const s: Record<Category, number> = { finances: 0, ties: 0, communication: 0, ds160: 0, intent: 0, profile: 0 };
    for (const q of QUESTIONS) {
      const a = answers[q.id];
      const w = q.weight ?? 1;
      if (a === "yes") s[q.category] += w;
      else if (a === "unsure") s[q.category] += w * 0.5;
    }
    return s;
  }, [answers]);

  const ranked = useMemo(
    () => (Object.entries(scores) as [Category, number][]).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]),
    [scores],
  );
  const topCategory = ranked[0]?.[0];

  // Save results once when the user first sees them
  useEffect(() => {
    if (!showResults || savedOnce || !userId || !topCategory) return;
    (async () => {
      const label = CATEGORY_META[topCategory].label;
      await supabase.from("refusal_decoder_responses" as never).insert({
        user_id: userId,
        consulate: null,
        answers,
        top_category: label,
      } as never);
      setSavedOnce(true);
      setInsightsRefreshKey((k) => k + 1);
    })();
  }, [showResults, savedOnce, userId, topCategory, answers]);

  const reset = () => {
    setAnswers({});
    setShowResults(false);
    setSavedOnce(false);
  };

  return (
    <div className="bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-red-50 via-white to-indigo-50">
        <div className="mx-auto max-w-4xl px-5 pt-16 pb-10 md:pt-24 md:pb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 border border-red-200 text-xs">
            <ShieldAlert className="h-3.5 w-3.5 text-red-600" />
            <span className="text-red-700 font-medium uppercase tracking-wider">214(b) Refusal Diagnostics</span>
          </div>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-balance leading-[1.05]">
            Refused under 214(b)? <em className="not-italic text-primary">Decode why - before you re-apply.</em>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-foreground/70 max-w-2xl leading-relaxed">
            A 12-question diagnostic built from 1,500+ interview debriefs. Answer honestly and you'll see the exact
            category that likely got you the blue slip - and what to fix before the second attempt.
          </p>
        </div>
      </section>

      {/* Quiz / Auth gate */}
      <section className="mx-auto max-w-3xl px-5 py-12">
        {authState === "loading" && <p className="text-center text-sm text-foreground/50">Loading...</p>}

        {authState === "signed-out" && (
          <div className="rounded-3xl border border-border bg-card p-8 md:p-12 text-center">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-accent grid place-items-center text-primary">
              <Lock className="h-7 w-7" />
            </div>
            <h2 className="mt-5 font-display text-2xl md:text-3xl font-semibold">
              Sign in to start your 214(b) diagnosis
            </h2>
            <p className="mt-3 text-foreground/70 max-w-md mx-auto text-sm md:text-base">
              We save your answers to your private account so you can pick up later and so we can share anonymized
              consulate-level insights with future students.
            </p>
            <button
              type="button"
              onClick={() => navigate({ to: "/auth", search: { redirect: "/refusal-decoder", google: false } })}
              className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition"
            >
              Sign In <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        )}

        {authState === "signed-in" && !showResults && (
          <>
            <div className="sticky top-16 z-10 bg-background/90 backdrop-blur py-3 border-b border-border">
              <div className="flex items-center justify-between text-xs text-foreground/60 mb-2">
                <span>
                  {totalAnswered} of {QUESTIONS.length} answered
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-gradient-cta transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            </div>

            <ol className="mt-8 space-y-6">
              {QUESTIONS.map((q, i) => (
                <li key={q.id} className="rounded-2xl border border-border bg-card p-5">
                  <p className="font-medium text-foreground">
                    <span className="text-primary font-semibold mr-2">Q{i + 1}.</span>
                    {q.q}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(["yes", "unsure", "no"] as const).map((opt) => {
                      const selected = answers[q.id] === opt;
                      const label = opt === "yes" ? "Yes" : opt === "no" ? "No" : "Not sure";
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                          className={
                            "px-4 py-2 rounded-full text-sm font-medium border transition " +
                            (selected
                              ? opt === "yes"
                                ? "bg-red-600 text-white border-red-600"
                                : opt === "no"
                                  ? "bg-emerald-600 text-white border-emerald-600"
                                  : "bg-amber-500 text-white border-amber-500"
                              : "bg-background border-border hover:border-primary/50 hover:bg-accent/40")
                          }
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ol>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4">
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-2 text-sm text-foreground/60 hover:text-foreground"
              >
                <RotateCcw className="h-4 w-4" /> Reset
              </button>
              <button
                type="button"
                disabled={totalAnswered === 0}
                onClick={() => {
                  setShowResults(true);
                  if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                See my diagnosis <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </>
        )}

        {authState === "signed-in" && showResults && (
          <Results ranked={ranked} topCategory={topCategory} onRetake={reset} />
        )}
      </section>

      {/* INSIGHTS SECTION */}
      <InsightsSection refreshKey={insightsRefreshKey} userId={userId} />

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 py-16 border-t border-border">
        <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Frequently asked questions</h2>
        <div className="mt-6 space-y-3">
          {FAQS.map((f) => (
            <details key={f.q} className="rounded-2xl border border-border bg-card p-5 group">
              <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between gap-4">
                <span>{f.q}</span>
                <span className="text-primary group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

function Results({
  ranked,
  topCategory,
  onRetake,
}: {
  ranked: [Category, number][];
  topCategory: Category | undefined;
  onRetake: () => void;
}) {
  if (!topCategory) {
    return (
      <div className="text-center py-16">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h2 className="mt-4 font-display text-2xl font-semibold">No obvious red flags</h2>
        <p className="mt-2 text-foreground/70 max-w-md mx-auto">
          Based on your answers we couldn't isolate a dominant refusal category. That usually means delivery or a subtle
          DS-160 flag. A single mock with a Specialist will surface it fast.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground font-semibold hover:opacity-90 transition"
          >
            Book a mock <ArrowRight className="h-4 w-4" />
          </Link>
          <button
            type="button"
            onClick={onRetake}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-border text-foreground font-medium hover:bg-accent transition"
          >
            <RotateCcw className="h-4 w-4" /> Retake
          </button>
        </div>
      </div>
    );
  }

  const top = CATEGORY_META[topCategory];
  const TopIcon = top.icon;
  const maxScore = ranked[0][1];

  return (
    <div>
      <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
        <div className="flex items-start gap-4">
          <div className={"h-14 w-14 rounded-2xl grid place-items-center border " + top.color}>
            <TopIcon className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wider text-primary">Most likely reason</p>
            <h2 className="mt-1 font-display text-2xl md:text-3xl font-semibold">{top.label}</h2>
            <p className="mt-2 text-foreground/70 text-sm md:text-base">
              Your answers cluster hardest here. This is where officers most likely lost trust in your case.
            </p>
          </div>
        </div>

        <div className="mt-8">
          <p className="text-sm font-semibold text-foreground">What to fix before the next attempt</p>
          <ul className="mt-3 space-y-2.5">
            {top.fixes.map((f, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-foreground/80">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {ranked.length > 1 && (
        <div className="mt-8">
          <p className="text-sm font-semibold text-foreground">Other categories to review</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            {ranked.slice(1, 4).map(([cat, score]) => {
              const meta = CATEGORY_META[cat];
              const Icon = meta.icon;
              const pct = Math.round((score / maxScore) * 100);
              return (
                <div key={cat} className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3">
                  <div className={"h-10 w-10 rounded-xl grid place-items-center border shrink-0 " + meta.color}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{meta.label}</p>
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary/60" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-10 rounded-3xl bg-gradient-to-br from-primary/10 via-sky-50 to-indigo-50 border border-primary/30 p-6 md:p-8">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-display text-xl md:text-2xl font-semibold">Ready for a proper re-attempt plan?</h3>
            <p className="mt-2 text-sm md:text-base text-foreground/70 max-w-2xl">
              Our Refusal Support plan is built exactly for this: root-cause analysis, 5 one-on-one mocks, DS-160
              line-by-line review, and a red-flag fix list - from Specialists who've handled 300+ refusals.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <Link
                to="/refusal-support"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground font-semibold hover:opacity-90 transition"
              >
                See Refusal Support plan <ArrowRight className="h-4 w-4" />
              </Link>
              <button
                type="button"
                onClick={onRetake}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-border bg-white text-foreground font-medium hover:bg-accent transition"
              >
                <RotateCcw className="h-4 w-4" /> Retake
              </button>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-6 text-center text-xs text-foreground/50">
        This is a self-diagnostic, not legal advice. Every case has nuance - a real Specialist will find what a
        checklist misses.
      </p>
    </div>
  );
}

function InsightsSection({ refreshKey, userId }: { refreshKey: number; userId: string | null }) {
  const [data, setData] = useState<{ category: string; count: number; total: number }[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [myLatest, setMyLatest] = useState<{ top_category: string; created_at: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const rows = await getRefusalDecoderAggregates();
        if (cancelled) return;
        setData(rows);
      } catch {
        if (cancelled) return;
        setData([]);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  useEffect(() => {
    if (!userId) {
      setMyLatest(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("refusal_decoder_responses" as never)
        .select("top_category, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setMyLatest((data as unknown as { top_category: string; created_at: string } | null) ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, refreshKey]);

  const total = data?.[0]?.total ?? 0;
  const hasData = !loading && data && data.length > 0 && total >= 1;

  return (
    <section className="bg-accent/20 border-y border-border">
      <div className="mx-auto max-w-4xl px-5 py-16 md:py-20">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-primary" />
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Insights from our students</p>
        </div>
        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight">
          What actually causes 214(b) refusals?
        </h2>
        <p className="mt-3 text-foreground/70 max-w-2xl text-sm md:text-base">
          Based on real diagnoses from students who've used this tool. No names or personal details are ever shown or
          stored publicly.
        </p>

        {myLatest && (
          <div className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
            <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-foreground">
                Your latest diagnosis: <span className="text-primary">{myLatest.top_category}</span>
              </p>
              <p className="text-xs text-foreground/60 mt-0.5">
                Recorded {new Date(myLatest.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        )}

        <div className="mt-8 rounded-3xl border border-border bg-card p-6 md:p-8">
          {loading && <p className="text-sm text-foreground/60">Loading insights...</p>}
          {!loading && !hasData && (
            <p className="text-sm text-foreground/60">No diagnoses recorded yet. Be the first to complete one above.</p>
          )}
          {hasData && (
            <div className="space-y-4">
              {data!
                .slice()
                .sort((a, b) => b.count - a.count)
                .map((row) => {
                  const pct = Math.round((row.count / row.total) * 100);
                  return (
                    <div key={row.category}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium">{row.category}</span>
                        <span className="text-foreground/60">{pct}%</span>
                      </div>
                      <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-gradient-cta" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              <p className="mt-4 text-xs text-foreground/50">Based on {total} diagnoses.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
